import { call, put, select, take } from 'redux-saga/effects';

import { GeneratorResult, OperationStatus, selectTyped } from '@pressreader/appstore';
import { selectBookmarksEnabled } from '@pressreader/global-config';
import { error } from '@pressreader/logger';
import { getUserCollections } from '@pressreader/social-api';
import { IUserCollection } from '@pressreader/social-types';
import { ExtractReturnType } from '@pressreader/types';

import { loadUserCollectionsBegan, loadUserCollectionsDone, loadUserCollectionsFailed, loadUserCollectionsFinishedActionList } from '../../actions';
import { selectUserCollections, selectUserCollectionsStatus } from '../../selectors';

function* getOrWaitForUserCollections(): GeneratorResult<IUserCollection[] | null> {
    const status: ReturnType<typeof selectUserCollectionsStatus> = yield select(selectUserCollectionsStatus);
    const collections: ReturnType<typeof selectUserCollections> = yield select(selectUserCollections);

    const isNonEmptyStatus = status != null && status !== OperationStatus.None;
    if (isNonEmptyStatus) {
        if (status === OperationStatus.Done) {
            return collections;
        }

        if (status === OperationStatus.Failed) {
            return null;
        }

        yield take([...loadUserCollectionsFinishedActionList]);

        return yield* getOrWaitForUserCollections();
    }

    return null;
}

function* loadUserCollections(force?: boolean) {
    try {
        const enabled = yield* selectTyped(selectBookmarksEnabled);
        if (!enabled) {
            yield put(loadUserCollectionsDone({ collections: [] }));
            return;
        }

        const status: ReturnType<typeof selectUserCollectionsStatus> = yield select(selectUserCollectionsStatus);
        const shouldForceLoad = force && status !== OperationStatus.Pending;
        const collectionsCached = shouldForceLoad ? null : yield* getOrWaitForUserCollections();

        yield put(loadUserCollectionsBegan());

        const isCollectionExist = Boolean(collectionsCached) && !force;
        const collections: ExtractReturnType<typeof getUserCollections> = isCollectionExist ? collectionsCached : yield call(getUserCollections);

        yield put(loadUserCollectionsDone({ collections }));
        return collections;
    } catch (e) {
        error(e);
        yield put(loadUserCollectionsFailed({ error: e }));
    }

    return [];
}

export { loadUserCollections };
